import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLazyQuery, useQuery } from '@apollo/client';
import { Container, Tab, Tabs, Typography } from '@material-ui/core';
import cx from 'classnames';
import differenceInBusinessDays from 'date-fns/differenceInBusinessDays';
import formatISO9075 from 'date-fns/formatISO9075';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Element, scroller } from 'react-scroll';

import cpdLogo from '../../../../assets/images/cpd-logo.png';
import bgIntro from '../../../../assets/images/news-bg.jpg';
import { BarChart } from '../../../../components/BarChart';
import { LineChart } from '../../../../components/LineChart';
import { RadarChart } from '../../../../components/RadarChart';
import RolesViewSwitcher from '../../../../components/RolesViewSwitcher';
import RotateYourPhoneCTA from '../../../../components/RotateYourPhoneCTA';
import { Select } from '../../../../components/Select';
import { useAlert } from '../../../../context/AlertProvider';
import {
  GET_CPD_CREDITS_STAT,
  GET_LIST_BASE,
  GET_LOGIN_USERS_STAT,
  GET_MANAGER_DETAILED_REPORT_BY_PATHWAYS,
  GET_MANAGER_DETAILED_REPORT_BY_PATHWAYS_CSV_LINK,
  GET_MANAGER_DETAILED_REPORT_BY_VIDEOS,
  GET_MANAGER_DETAILED_REPORT_BY_VIDEOS_CSV_LINK,
  GET_REPORT_BY_LEARNER,
  GET_TABLES_ANALYTICS,
  GET_VIDEOS_WATCHED_BY_CATEGORY,
  GET_VIDEOS_WATCHED_COUNT_STAT,
  GET_VIEWED_HOUR_STAT,
} from '../../../../graphql/analytics';
import { GET_TEAMS_LIST, GET_TEAMS_MEMBERS } from '../../../../graphql/user';
import {
  CpdCreditsFilterArgs,
  DetailedReportByPathwaysItem,
  DetailedReportByVideosItem,
  GetMembersByTeamIdsArgs,
  GetTablesAnalytics,
  GetTablesAnalyticsArgs,
  GroupByPeriod,
  LearnerInfo,
  LoginUsersFilterArgs,
  PaginationOption,
  PathwaysGroupedRes,
  Query,
  QueryGetManagerDetailedReportByPathwaysArgs,
  QueryGetManagerDetailedReportByVideosArgs,
  QueryGroupedByUsersStatArgs,
  QueryListBaseVideoCategoriesArgs,
  StatisticFilterArgs,
  TypeAnalytic,
  VideosGroupedRes,
  ViewedHoursFilterArgs,
  ViewedVideosFilterArgs,
} from '../../../../types';
import { Header } from '../../../DiscoverPage/components/Header';
import {
  EXPORT_USERS_TABLE_HEADING,
  getPathwaysAdvancedTableHeading,
  getVideosAdvancedTableHeading,
  GRAPH_TYPE,
  initialDatesFilter,
  initialTeamFilter,
  initialUsersFilter,
  MANAGER_VIEW_TYPES,
  PATHWAYS_TABLE_HEADING,
  pathwaysExportAdvancedTableHeading,
  USERS_TABLE_HEADING,
  VIDEOS_TABLE_HEADING,
  videosExportAdvancedTableHeading,
  sortKeyType,
} from '../../const';
import {
  createUsersTableData,
  downloadCsvDirectly,
  getTimeRangeFilters,
  transformAdvancedPathwayToTableData,
  transformAdvancedVideoToTableData,
  transformLoginUserStat,
  transformPathwayToTableData,
  transformVideosCategoryData,
  transformVideosWatchedData,
  transformVideoToTableData,
  transformViewingHoursData,
} from '../../helpers';
import useStyles from '../../styles';
import Card from '../Card';
import ChosenGraph from '../ChosenGraph';
import { PathwayUsersTable } from '../PathwayUsersTable';
import PickerWithRanges from '../PickerWithRanges';
import TabPanel, { a11yProps } from '../TabPanel/TabPanel';
import AnalyticsTable from '../Table';
import { VideoUsersTable } from '../VideoUsersTable';

const timeRangeFilters = getTimeRangeFilters();

interface ManagerProps {
  isMobile?: boolean;
  tenantTitle?: string;
  tenantName?: string | null;
  userInfo?: LearnerInfo;
}

const Manager: FC<ManagerProps> = ({ isMobile, tenantTitle, tenantName }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { showAlert } = useAlert();
  const [chosenGraphType, setChosenGraph] = useState<GRAPH_TYPE>(GRAPH_TYPE.Hours);
  const [selectedTeam, setSelectedTeam] = useState(initialTeamFilter);
  const [selectedUser, setSelectedUser] = useState(initialUsersFilter);
  const [selectedTimeRange, setTimeRange] = useState(initialDatesFilter);
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedVideo, setSelectedVideo] = useState<VideosGroupedRes | null>(null);
  const [selectedPathway, setSelectedPathway] = useState<PathwaysGroupedRes | null>(null);
  const [viewType, setViewType] = useState<MANAGER_VIEW_TYPES>(MANAGER_VIEW_TYPES.Analytic);
  const [selectedTimeRangeFilter, setSelectedTimeRangeFilter] = useState(timeRangeFilters.all);
  const [isAdvancedView, setIsAdvancedView] = useState(false);
  const [sortKey, setSortKey] = useState<sortKeyType>(null);

  const selectedTimeRangeValue = selectedTimeRange?.value;

  const pathwaysAdvancedTableHeading = useMemo(() => getPathwaysAdvancedTableHeading(t, classes), [
    t,
    classes,
  ]);

  const videosAdvancedTableHeading = useMemo(() => getVideosAdvancedTableHeading(t, classes), [
    t,
    classes,
  ]);

  useEffect(() => {
    const filter = timeRangeFilters[selectedTimeRangeValue];

    if (filter) {
      setSelectedTimeRangeFilter(filter);
    }
  }, [selectedTimeRangeValue]);

  const { loading: loadingTeams, data: teamsData } = useQuery<Pick<Query, 'getTeamsList'>>(
    GET_TEAMS_LIST,
  );

  const teams = useMemo(() => teamsData?.getTeamsList || [], [teamsData]);

  // @ts-ignore
  const teamIds = teams.map((item) => Number(item.id));

  const teamFilter = selectedTeam?.value === 'all' ? teamIds : [Number(selectedTeam?.value)];
  const { data: teamData, loading: loadingTeamData } = useQuery<
    Pick<Query, 'getMembersByTeamIdsArray'>,
    GetMembersByTeamIdsArgs
  >(GET_TEAMS_MEMBERS, {
    variables: {
      team_ids: teamFilter,
    },
  });
  const teamMembers = teamData?.getMembersByTeamIdsArray || [];
  const userIds = useMemo(
    () =>
      teamData?.getMembersByTeamIdsArray
        ? teamData.getMembersByTeamIdsArray.map((item) => +item?.id)
        : [],
    [teamData],
  );
  const groupBy = selectedTimeRangeFilter.groupBy;
  const usersFilter = useMemo(
    () =>
      selectedUser[0]?.value === 'all' ? userIds : selectedUser.map((i) => parseInt(i?.value)),
    [selectedUser, userIds],
  );

  const filteredUsers = teamMembers
    ? teamMembers.filter((item) => usersFilter?.find((userId) => +item?.id === userId))
    : [];

  const shortFilter = {
    user_ids: usersFilter,
    type: TypeAnalytic.Manager,
    ...(Boolean(selectedTimeRangeFilter.filter) && {
      date: selectedTimeRangeFilter.filter,
    }),
  };

  const longFilter = {
    user_ids: usersFilter,
    ...(Boolean(selectedTimeRangeFilter.filter) && {
      date: selectedTimeRangeFilter.filter,
    }),
    group_by: groupBy,
  };

  const {
    data: videosWatchedByCategoryRes,
    loading: loadingVideosWatchedByCategory,
    error: errorVideosWatchedByCategory,
  } = useQuery<Pick<Query, 'VideosWatchedByCathegoryStat'>, StatisticFilterArgs>(
    GET_VIDEOS_WATCHED_BY_CATEGORY,
    {
      variables: {
        statisticFilter: shortFilter,
      },
      skip: !userIds,
      fetchPolicy: 'network-only',
    },
  );

  const {
    data: viewedHourStatRes,
    loading: loadingViewedHourStat,
    error: errorViewedHourStat,
  } = useQuery<Pick<Query, 'ViewedHoursStat'>, ViewedHoursFilterArgs>(GET_VIEWED_HOUR_STAT, {
    variables: {
      viewedHoursFilter: { ...longFilter, type: TypeAnalytic.Manager },
    },
    skip: !userIds,
    fetchPolicy: 'network-only',
  });

  const {
    data: videosWatchedCountStatRes,
    loading: loadingVideosWatchedCountStat,
    error: errorVideosWatchedCountStat,
  } = useQuery<Pick<Query, 'VideosWatchedCountStat'>, ViewedVideosFilterArgs>(
    GET_VIDEOS_WATCHED_COUNT_STAT,
    {
      variables: {
        viewedVideosFilter: { ...longFilter, type: TypeAnalytic.Manager },
      },
      skip: !userIds,
      fetchPolicy: 'network-only',
    },
  );

  const {
    data: cpdCreditsStatRes,
    loading: loadingCpdCreditsStat,
    error: errorCpdCreditsStat,
  } = useQuery<Pick<Query, 'CpdCreditsStat'>, CpdCreditsFilterArgs>(GET_CPD_CREDITS_STAT, {
    variables: {
      cpdFilter: shortFilter,
    },
    skip: !userIds,
    fetchPolicy: 'network-only',
  });

  const {
    data: loginUsersStatRes,
    loading: loadingLoginUsersStat,
    error: errorLoginUsersStat,
  } = useQuery<Pick<Query, 'LoginUsersStat'>, LoginUsersFilterArgs>(GET_LOGIN_USERS_STAT, {
    variables: {
      loginStatFilter: longFilter,
    },
    skip: !userIds,
    fetchPolicy: 'network-only',
  });

  const { data: listBaseRes, loading: loadingListBase, error: errorListBase } = useQuery<
    Pick<Query, 'listBaseVideoCategories'>,
    QueryListBaseVideoCategoriesArgs
  >(GET_LIST_BASE, {
    skip: !userIds,
  });

  const { data: tablesData, loading: loadingTablesData } = useQuery<
    GetTablesAnalytics,
    GetTablesAnalyticsArgs
  >(GET_TABLES_ANALYTICS, {
    variables: {
      pathwaysGroupedFilter: shortFilter,
      videosGroupedFilter: shortFilter,
    },
    skip: !userIds,
    fetchPolicy: 'network-only',
  });

  const { data: learnersData, loading: loadingLearnersData } = useQuery<
    Pick<Query, 'UsersGroupedStat'>,
    QueryGroupedByUsersStatArgs
  >(GET_REPORT_BY_LEARNER, {
    variables: {
      filter: {
        pagination_option: {
          page: 1,
          count_items_on_page: usersFilter?.length,
        },
        user_ids: usersFilter,
        ...(Boolean(selectedTimeRangeFilter.filter) && {
          date: selectedTimeRangeFilter.filter,
        }),
      },
    },
    skip: !userIds,
    fetchPolicy: 'network-only',
  });

  const [
    getAdvancedPathwaysData,
    { data: advancedPathwayData, loading: loadingAdvancedPathwayData },
  ] = useLazyQuery<
    Pick<Query, 'getManagerDetailedReportByPathways'>,
    QueryGetManagerDetailedReportByPathwaysArgs
  >(GET_MANAGER_DETAILED_REPORT_BY_PATHWAYS, {
    fetchPolicy: 'no-cache',
  });

  const [getAdvancedPathwaysDataLink, { loading: loadingAdvancedPathwayDataLink }] = useLazyQuery<
    Pick<Query, 'getDetailedReportByPathwaysCsvLink'>,
    QueryGetManagerDetailedReportByPathwaysArgs
  >(GET_MANAGER_DETAILED_REPORT_BY_PATHWAYS_CSV_LINK, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (data?.getDetailedReportByPathwaysCsvLink) {
        const currDate = Math.floor(Date.now() / 1000);
        const fileName = `${t('page.analytics.pathwaysAdvanced')?.replace(/ /g, '_')}-${currDate}`;
        downloadCsvDirectly(data.getDetailedReportByPathwaysCsvLink, fileName);
      }
    },
  });

  const advancedPathwayTableData = useMemo(() => {
    const data = advancedPathwayData?.getManagerDetailedReportByPathways?.items
      ? (advancedPathwayData.getManagerDetailedReportByPathways.items.filter((item) =>
          Boolean(item),
        ) as DetailedReportByPathwaysItem[])
      : [];
    return data?.map((item) => transformAdvancedPathwayToTableData(item)) || [];
  }, [advancedPathwayData]);

  const [
    getAdvancedVideosData,
    { data: advancedVideosData, loading: loadingAdvancedVideosData },
  ] = useLazyQuery<
    Pick<Query, 'getManagerDetailedReportByVideos'>,
    QueryGetManagerDetailedReportByVideosArgs
  >(GET_MANAGER_DETAILED_REPORT_BY_VIDEOS, {
    fetchPolicy: 'no-cache',
  });

  const [getAdvancedVideosDataLink, { loading: loadingAdvancedVideosDataLink }] = useLazyQuery<
    Pick<Query, 'getDetailedReportByVideosCsvLink'>,
    QueryGetManagerDetailedReportByPathwaysArgs
  >(GET_MANAGER_DETAILED_REPORT_BY_VIDEOS_CSV_LINK, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (data?.getDetailedReportByVideosCsvLink) {
        const currDate = Math.floor(Date.now() / 1000);
        const fileName = `${t('page.analytics.videosAdvanced')?.replace(/ /g, '_')}-${currDate}`;
        downloadCsvDirectly(data.getDetailedReportByVideosCsvLink, fileName);
      }
    },
  });

  const advancedVideosTableData = useMemo(() => {
    const data = advancedVideosData?.getManagerDetailedReportByVideos?.items
      ? (advancedVideosData.getManagerDetailedReportByVideos.items.filter((item) =>
          Boolean(item),
        ) as DetailedReportByVideosItem[])
      : [];
    return data?.map((item) => transformAdvancedVideoToTableData(item)) || [];
  }, [advancedVideosData]);

  const changeTeam = (options) => {
    if (!options) return;
    setSelectedTeam(options);
  };

  const changeUser = (options) => {
    if (!options) return;
    if (options.length === 0) {
      setSelectedUser(initialUsersFilter);
      return;
    }
    if (selectedUser.length === 1 && selectedUser[0]?.value === 'all') {
      options.splice(0, 1);
    } else if (options.length > 1) {
      const index = options.findIndex((i) => i === 'all' || i?.value === 'all');
      if (index !== -1) {
        options = [options[index]];
      }
    }
    setSelectedUser(options);
  };

  const changeTimeRange = (option) => {
    if (!option) return;
    setTimeRange(option);
  };

  const handleChangeIndex = (event, newValue) => {
    setIsAdvancedView(false);
    setTabIndex(newValue);
    setSortKey(null);
  };

  const handleSortChanged = (sortValue: sortKeyType) => {
    setSortKey(sortValue);
  };

  const viewingHoursData = viewedHourStatRes?.ViewedHoursStat;
  const videosWatchedData = videosWatchedCountStatRes?.VideosWatchedCountStat;
  const cpdCreditsData = cpdCreditsStatRes?.CpdCreditsStat;
  const videosWatchedByCategoryData = videosWatchedByCategoryRes?.VideosWatchedByCathegoryStat;
  const listOfAreas = listBaseRes?.listBaseVideoCategories || [];
  const loginUsersStat = loginUsersStatRes?.LoginUsersStat;
  const viewingHoursDataGrByDates = viewingHoursData?.grouped_by_dates || [];
  const loginUserStatGrByDates = loginUsersStat?.grouped_by_dates || [];
  const videosWatchedDataGrByDates = videosWatchedData?.grouped_by_dates || [];

  const preparedLoginUserStat = transformLoginUserStat(
    chosenGraphType,
    isMobile,
    loginUserStatGrByDates,
  );
  const preparedViewingHoursData = transformViewingHoursData(
    chosenGraphType,
    isMobile,
    viewingHoursDataGrByDates,
  );
  const preparedVideosWatchedData = transformVideosWatchedData(
    chosenGraphType,
    isMobile,
    videosWatchedDataGrByDates,
  );
  const chosenGraphData = {
    logins: preparedLoginUserStat,
    hours: preparedViewingHoursData,
    videos: preparedVideosWatchedData,
  };

  const teamItems = teams ? teams.map((m) => ({ value: m?.id, display: m?.name })) : [];
  const teamSelectItems = [initialTeamFilter, ...(teamItems && teamItems)];

  const userItems = teamMembers
    ? teamMembers.map((m) => ({ value: +m?.id, display: m?.name }))
    : [];
  const userSelectItems = [...initialUsersFilter, ...(userItems && userItems)];

  const timeRangeItems = Object.keys(timeRangeFilters).map((k) => ({
    value: k,
    display: timeRangeFilters[k].display,
  }));

  const videosCategoryUsers =
    filteredUsers &&
    filteredUsers.filter((member) => {
      const memberId = +member?.id;
      return usersFilter.includes(memberId);
    });

  const usersTableData = videosCategoryUsers
    ? createUsersTableData(
        videosCategoryUsers,
        teamItems,
        learnersData?.UsersGroupedStat?.users_stat,
      )
    : [];

  const handleViewVideoDetails = (videoItem) => (e) => {
    e?.stopPropagation();
    setSelectedVideo(videoItem);
    setViewType(MANAGER_VIEW_TYPES.VideoTable);
    scroller.scrollTo('analytic-wrapper', {
      duration: 0,
    });
  };

  const handleViewPathwayDetails = (pathwayItem) => (e) => {
    e?.stopPropagation();
    setSelectedPathway(pathwayItem);
    setViewType(MANAGER_VIEW_TYPES.PathwayTable);
    scroller.scrollTo('analytic-wrapper', {
      duration: 0,
    });
  };

  const pathwaysTableData = useMemo(() => {
    const pathwaysGroupedStat = tablesData?.PathwaysGroupedStat;
    return (
      pathwaysGroupedStat?.map((item) =>
        transformPathwayToTableData(item, handleViewPathwayDetails),
      ) || []
    );
  }, [tablesData]);

  const videosTableData = useMemo(() => {
    const videosGroupedStat = tablesData?.VideosGroupedStat;
    return (
      videosGroupedStat?.map((item) => transformVideoToTableData(item, handleViewVideoDetails)) ||
      []
    );
  }, [tablesData]);

  const preparedVideosCategoryData = transformVideosCategoryData(
    listOfAreas,
    videosWatchedByCategoryData,
    filteredUsers,
    teams,
  );
  // eslint-disable-next-line
  const { category, ...radarKeysObject } = preparedVideosCategoryData[0] || {};

  const filtersWrapRef = useRef(null);
  const filtersWrapEl: Element | null = filtersWrapRef?.current;
  // @ts-ignore
  const filtersWrapWidth = filtersWrapEl?.clientWidth;

  const toggleView = () => {
    setIsAdvancedView((prevState) => !prevState);
  };

  const hasError = !!(
    errorListBase ||
    errorLoginUsersStat ||
    errorVideosWatchedCountStat ||
    errorViewedHourStat ||
    errorVideosWatchedByCategory ||
    errorCpdCreditsStat
  );

  const loading =
    loadingTeams ||
    loadingVideosWatchedByCategory ||
    loadingViewedHourStat ||
    loadingVideosWatchedCountStat ||
    loadingCpdCreditsStat ||
    loadingLoginUsersStat ||
    loadingListBase ||
    hasError;

  const goBackToAnalytic = useCallback(() => {
    setViewType(MANAGER_VIEW_TYPES.Analytic);
    setTimeout(() => {
      scroller.scrollTo('manager-tabs', {
        duration: 0,
        offset: -100,
      });
    }, 0);
  }, [setViewType]);

  const fetchAdvancedPathwayData = useCallback(
    (paginationData: PaginationOption) => {
      getAdvancedPathwaysData({
        variables: {
          filter: {
            learner_ids: usersFilter,
            team_ids: selectedTeam?.value === 'all' ? [] : [Number(selectedTeam?.value)],
            date: selectedTimeRangeFilter?.filter || null,
            pagination_option: paginationData,
          },
        },
      });
    },
    [getAdvancedPathwaysData, usersFilter, selectedTeam, selectedTimeRangeFilter],
  );

  const handleExportAdvancedPathwayAnalytic = () => {
    getAdvancedPathwaysDataLink({
      variables: {
        filter: {
          learner_ids: usersFilter,
          team_ids: selectedTeam?.value === 'all' ? [] : [Number(selectedTeam?.value)],
          date: selectedTimeRangeFilter?.filter || null,
        },
      },
    });

    showAlert({ message: t('alert.theDownloadWillStartSoon') });
  };

  const fetchAdvancedVideoData = useCallback(
    (paginationData: PaginationOption) => {
      getAdvancedVideosData({
        variables: {
          filter: {
            learner_ids: usersFilter,
            team_ids: selectedTeam?.value === 'all' ? [] : [Number(selectedTeam?.value)],
            date: selectedTimeRangeFilter?.filter || null,
            pagination_option: paginationData,
          },
        },
      });
    },
    [getAdvancedVideosData, usersFilter, selectedTeam, selectedTimeRangeFilter],
  );

  const handleExportAdvancedVideoAnalytic = () => {
    getAdvancedVideosDataLink({
      variables: {
        filter: {
          learner_ids: usersFilter,
          team_ids: selectedTeam?.value === 'all' ? [] : [Number(selectedTeam?.value)],
          date: selectedTimeRangeFilter?.filter || null,
        },
      },
    });

    showAlert({ message: t('alert.theDownloadWillStartSoon') });
  };

  if (viewType === MANAGER_VIEW_TYPES.VideoTable && selectedVideo?.story_id) {
    return (
      <>
        <Header
          title={t('page.analytics.heading')}
          wrapperClass={classes.headerWrapper}
          contentWrapperClass={classes.headerContainer}
          titleClass={classes.headerTitle}
          image={bgIntro}
          tenantName={tenantName}
        />
        <VideoUsersTable
          title={selectedVideo?.title}
          description={selectedVideo?.description}
          storyId={selectedVideo.story_id.toString()}
          goBack={goBackToAnalytic}
          date={selectedTimeRangeFilter.filter}
          userIds={usersFilter || []}
          total={selectedVideo?.users_started}
          duration={selectedVideo?.video_duration}
        />
      </>
    );
  }

  if (viewType === MANAGER_VIEW_TYPES.PathwayTable && selectedPathway) {
    return (
      <>
        <div className={classes.wrapper}>
          <Header
            title={t('page.analytics.heading')}
            wrapperClass={classes.headerWrapper}
            contentWrapperClass={classes.headerContainer}
            titleClass={classes.headerTitle}
            image={bgIntro}
          />
        </div>
        <PathwayUsersTable
          title={selectedPathway?.pathway?.name}
          description={selectedPathway?.pathway?.description}
          goBack={goBackToAnalytic}
          date={selectedTimeRangeFilter.filter}
          userIds={usersFilter || []}
          pathwayId={selectedPathway?.pathway?.id}
          total={selectedPathway?.users_started}
        />
      </>
    );
  }

  const pickerSubmitHandler = (from, to) => {
    let groupBy = GroupByPeriod.Month;
    const diff = differenceInBusinessDays(to, from);

    if (diff < 90) groupBy = GroupByPeriod.Week;
    if (diff < 30) groupBy = GroupByPeriod.Day;

    // @ts-ignore
    setSelectedTimeRangeFilter({
      display: 'Custom',
      // @ts-ignore
      filter: { from: formatISO9075(from), to: formatISO9075(to) },
      groupBy: groupBy,
    });
  };

  return (
    <>
      <Helmet>
        <title>
          {t('page.analytics.heading')} - {tenantTitle}
        </title>
      </Helmet>
      <div className={classes.wrapper}>
        <Header
          title={t('page.analytics.heading')}
          wrapperClass={classes.headerWrapper}
          contentWrapperClass={classes.headerContainer}
          titleClass={classes.headerTitle}
          tenantName={tenantName}
        >
          <Container>
            <Typography className={classes.headerDescription}>
              {t('page.analytics.managerDesc')}
            </Typography>
          </Container>
        </Header>
      </div>
      <Container className={classes.viewSwitcherWrap}>
        <RolesViewSwitcher
          rightBtnLink="/analytics"
          leftBtnLink="/analytics/dashboard"
          wrapperClass={classes.viewSwitcher}
        />
      </Container>
      <Container className={cx(classes.container, classes.managerAnalytics)}>
        <div className={classes.filtersWrapper} ref={filtersWrapRef}>
          <Select
            data-testid="teamsSelect"
            className={classes.filter}
            id="team-select"
            value={selectedTeam}
            onChange={changeTeam}
            items={teamSelectItems as []}
            anchorEl={filtersWrapEl}
            wrapperWidth={filtersWrapWidth}
            search
            popperLabel={t('select.team')}
          />

          <Select
            data-testid="learnersSelect"
            className={classes.filter}
            id="user-select"
            value={selectedUser}
            onChange={changeUser}
            items={userSelectItems}
            anchorEl={filtersWrapEl}
            wrapperWidth={filtersWrapWidth}
            multiple
            search
            popperLabel={t('select.learners')}
          />

          <PickerWithRanges
            id="range-picker"
            anchorEl={filtersWrapEl}
            options={timeRangeItems}
            value={selectedTimeRange}
            onChange={changeTimeRange}
            onSubmit={pickerSubmitHandler}
            wrapperWidth={filtersWrapWidth}
            isMobile={isMobile}
          />
        </div>

        <div className={classes.innerWrapper}>
          <div className={classes.smallCardsWrap}>
            <Card
              title={t('page.analytics.viewingHours')}
              value={viewingHoursData?.total_view_duration}
              toggle={!isMobile}
              withExpand={!isMobile}
              type="hours"
              activeType={chosenGraphType}
              onClick={setChosenGraph}
              loading={loading}
              data-testid="hoursCard"
            >
              <LineChart name="smallHours" data={preparedViewingHoursData} groupBy={groupBy} />
            </Card>

            <Card
              title={t('page.analytics.learningSessions')}
              value={loginUsersStat?.total_count_logins}
              toggle={!isMobile}
              withExpand={!isMobile}
              type="logins"
              activeType={chosenGraphType}
              onClick={setChosenGraph}
              loading={loading}
              data-testid="loginsCard"
            >
              <LineChart name="smallLogins" data={preparedLoginUserStat} groupBy={groupBy} />
            </Card>

            <Card
              title={t('page.analytics.videoesWatched')}
              value={videosWatchedData?.total_count_viewed_videos}
              toggle={!isMobile}
              withExpand={!isMobile}
              type="videos"
              activeType={chosenGraphType}
              onClick={setChosenGraph}
              loading={loading}
              data-testid="videosCard"
            >
              <BarChart name="smallVideos" data={preparedVideosWatchedData} />
            </Card>

            <Card
              title={t('page.analytics.cpdCredits')}
              value={cpdCreditsData?.count}
              toggle={false}
              type="cpd"
              activeType={null}
              onClick={null}
              loading={loading}
              chart={false}
              data-testid="cpdCard"
            >
              <img src={cpdLogo} alt="CPD certification" />
            </Card>
          </div>

          <Card
            className={classes.radarCard}
            title={t(
              Object.keys(radarKeysObject)?.length > 1
                ? 'page.analytics.teamFinanceProfiles'
                : 'page.analytics.teamFinanceProfile',
            )}
            value={null}
            toggle={false}
            type={null}
            activeType={null}
            onClick={null}
            loading={loading}
          >
            <div className={classes.radarWrapper}>
              <RadarChart
                data={preparedVideosCategoryData}
                keys={Object.keys(radarKeysObject) || []}
              />
            </div>
          </Card>
        </div>

        <RotateYourPhoneCTA
          minRequiredWidth={800}
          title={t('page.analytics.rotateYourPhone')}
          wrapperClass={classes.rotateWrapper}
        />

        {!isMobile && (
          <ChosenGraph
            type={chosenGraphType}
            data={chosenGraphData}
            groupBy={groupBy}
            loading={loading}
            // @ts-ignore
            from={selectedTimeRangeFilter?.filter?.from}
            // @ts-ignore
            to={selectedTimeRangeFilter?.filter?.to}
          />
        )}

        <Element name="manager-tabs">
          <div className={classes.tableWrap}>
            <div className={classes.anchor} id="learners" />
            <Tabs
              value={tabIndex}
              onChange={handleChangeIndex}
              textColor="inherit"
              aria-label="analytics table tabs"
            >
              <Tab
                data-testid="learnersTab"
                label={t('page.analytics.byLearner')}
                {...a11yProps(0)}
              />
              <Tab
                data-testid="pathwaysTab"
                label={t('page.analytics.byPathway')}
                {...a11yProps(1)}
              />
              <Tab data-testid="videosTab" label={t('page.analytics.byVideo')} {...a11yProps(2)} />
            </Tabs>

            <TabPanel value={tabIndex} index={0}>
              <AnalyticsTable
                headingData={USERS_TABLE_HEADING}
                exportHeadingData={EXPORT_USERS_TABLE_HEADING}
                bodyData={usersTableData}
                tab={tabIndex}
                label={t('page.analytics.learners')}
                tenantTitle={tenantTitle}
                isLoading={loadingLearnersData || loadingTeamData}
                name="learnersTable"
                sortKey={sortKey}
                handleSortChanged={handleSortChanged}
              />
            </TabPanel>

            <TabPanel value={tabIndex} index={1}>
              {isAdvancedView ? (
                <AnalyticsTable
                  headingData={pathwaysAdvancedTableHeading}
                  exportHeadingData={pathwaysExportAdvancedTableHeading}
                  bodyData={advancedPathwayTableData}
                  tab={3}
                  label={t('page.analytics.pathwaysAdvanced')}
                  tenantTitle={tenantTitle}
                  handleChangeView={toggleView}
                  isAdvancedView={isAdvancedView}
                  dataCount={advancedPathwayData?.getManagerDetailedReportByPathways?.count || 0}
                  fetchData={fetchAdvancedPathwayData}
                  isLoading={loadingAdvancedPathwayData}
                  handleExportCsv={handleExportAdvancedPathwayAnalytic}
                  isExportDisabled={loadingAdvancedPathwayDataLink}
                />
              ) : (
                <AnalyticsTable
                  headingData={PATHWAYS_TABLE_HEADING}
                  bodyData={pathwaysTableData}
                  tab={tabIndex}
                  label={t('page.analytics.pathways')}
                  tenantTitle={tenantTitle}
                  handleChangeView={toggleView}
                  isAdvancedView={isAdvancedView}
                  isLoading={loadingTablesData}
                  name="pathwaysTable"
                  sortKey={sortKey}
                  handleSortChanged={handleSortChanged}
                />
              )}
            </TabPanel>

            <TabPanel value={tabIndex} index={2}>
              {isAdvancedView ? (
                <AnalyticsTable
                  headingData={videosAdvancedTableHeading}
                  exportHeadingData={videosExportAdvancedTableHeading}
                  bodyData={advancedVideosTableData}
                  tab={4}
                  label={t('page.analytics.videosAdvanced')}
                  tenantTitle={tenantTitle}
                  handleChangeView={toggleView}
                  isAdvancedView={isAdvancedView}
                  fetchData={fetchAdvancedVideoData}
                  dataCount={advancedVideosData?.getManagerDetailedReportByVideos?.count || 0}
                  isLoading={loadingAdvancedVideosData}
                  handleExportCsv={handleExportAdvancedVideoAnalytic}
                  isExportDisabled={loadingAdvancedVideosDataLink}
                />
              ) : (
                <AnalyticsTable
                  headingData={VIDEOS_TABLE_HEADING}
                  bodyData={videosTableData}
                  tab={tabIndex}
                  label={t('page.analytics.videos')}
                  tenantTitle={tenantTitle}
                  handleChangeView={toggleView}
                  isAdvancedView={isAdvancedView}
                  isLoading={loadingTablesData}
                  sortKey={sortKey}
                  handleSortChanged={handleSortChanged}
                />
              )}
            </TabPanel>
          </div>
        </Element>
      </Container>
    </>
  );
};

export default Manager;
